import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Wallet, Users, Trash2 } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

export default function UserGroups() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [quotaModal, setQuotaModal] = useState({ open: false, groupId: null, type: 'topup' });
  const [formData, setFormData] = useState({ name: '', contactName: '', contactPhone: '', contactEmail: '', contractEndDate: '', initialQuota: 0, remark: '', status: 'ACTIVE' });
  const [quotaAmount, setQuotaAmount] = useState(0);

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401) { navigate('/login'); return; }
      const data = await res.json();
      if (Array.isArray(data)) setGroups(data); else setGroups([]);
    } catch (error) {
      console.error("Failed to fetch groups", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = editingGroup 
      ? `${API_URL}/admin/groups/${editingGroup.id}`
      : `${API_URL}/admin/groups`;
    
    const method = editingGroup ? 'PATCH' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        setIsModalOpen(false);
        setEditingGroup(null);
        setFormData({ name: '', contactName: '', contactPhone: '', contactEmail: '', contractEndDate: '', initialQuota: 0, remark: '', status: 'ACTIVE' });
        fetchGroups();
      }
    } catch (error) {
      console.error("Failed to save group", error);
    }
  };

  const handleQuotaSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/admin/groups/${quotaModal.groupId}/quota`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ amount: quotaAmount, type: quotaModal.type })
      });
      if (res.ok) {
        setQuotaModal({ ...quotaModal, open: false });
        fetchGroups();
      }
    } catch (e) {
      console.error(e);
    }
  };

  
  const handleDeleteGroup = async (group) => {
    if(!confirm(`确定要删除用户组 "${group.name}" 吗？注意：如果组内还有用户，可能无法删除。`)) return;
    try {
      const res = await fetch(`/api/admin/groups/${group.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if(res.ok) {
        fetchGroups();
      } else {
        const err = await res.json();
        alert(err.error || '删除失败');
      }
    } catch(e) {
      alert('删除出错');
    }
  };
  
  const openEdit = (group) => {
    setEditingGroup(group);
    setFormData({ 
      name: group.name, 
      contactName: group.contactName || '',
      contactPhone: group.contactPhone || '',
      contactEmail: group.contactEmail || '',
      contractEndDate: group.contractEndDate ? group.contractEndDate.split('T')[0] : '',
      initialQuota: group.initialQuota, 
      remark: group.remark || '',
      status: group.status || 'ACTIVE'
    });
    setIsModalOpen(true);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">用户组 (公司) 管理</h1>
        <button 
          onClick={() => { setEditingGroup(null); setFormData({ name: '', contactName: '', contactPhone: '', contactEmail: '', contractEndDate: '', initialQuota: 0, remark: '', status: 'ACTIVE' }); setIsModalOpen(true); }}
          className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={18} className="mr-2" /> 新建用户组
        </button>
      </div>

      <div className="bg-white dark:bg-slate-800/50 border border-gray-100 dark:border-slate-700 rounded-xl shadow overflow-hidden">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-100 dark:border-slate-700">
            <tr>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">ID</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">名称</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">联系信息</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">额度 (已用/总量)</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">用户数</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">状态</th>
              <th className="p-4 text-sm font-semibold text-gray-600 dark:text-gray-400">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
            {groups.map(group => (
              <tr key={group.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/30">
                <td className="p-3 text-gray-600 dark:text-gray-400">#{group.id}</td>
                <td className="p-3 font-medium text-gray-900 dark:text-gray-200">{group.name}</td>
                <td className="p-3 text-gray-600 dark:text-gray-400">
                  {group.contactName && <div className="font-medium text-gray-800 dark:text-gray-200">{group.contactName}</div>}
                  {group.contactPhone && <div className="text-xs text-gray-500">{group.contactPhone}</div>}
                  {group.contactEmail && <div className="text-xs text-gray-500">{group.contactEmail}</div>}
                  {(!group.contactName && !group.contactPhone && !group.contactEmail) && '-'}
                </td>
                <td className="p-3 text-gray-600 dark:text-gray-400">
                  <span className={group.quotaUsed > group.initialQuota ? 'text-red-500' : 'text-green-600'}>
                    {group.quotaUsed}
                  </span>
                  <span className="text-gray-400 mx-1">/</span>
                  {group.initialQuota}
                </td>
                <td className="p-3 text-gray-600 dark:text-gray-400">{group._count?.users || 0}</td>
                <td className="p-3">
                  <span className={`px-2 py-1 rounded text-sm ${group.status === 'ACTIVE' ? 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400'}`}>
                    {group.status === 'ACTIVE' ? '正常' : '禁用'}
                  </span>
                </td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => openEdit(group)} className="text-blue-600 hover:bg-blue-50 p-2 rounded" title="编辑">
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => setQuotaModal({ open: true, groupId: group.id, type: 'topup' })} className="text-green-600 hover:bg-green-50 p-2 rounded" title="充值">
                    <Wallet size={14} />
                  </button>
                  <button onClick={() => handleDeleteGroup(group)} className="text-red-500 hover:bg-red-50 p-2 rounded" title="删除">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200 dark:border-slate-700 p-6 rounded-2xl w-full max-w-2xl shadow-2xl">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">{editingGroup ? '编辑用户组' : '新建用户组'}</h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">公司名称</label>
                <input 
                  type="text" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  value={formData.name} 
                  onChange={e => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">联系人姓名</label>
                <input 
                  type="text" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  value={formData.contactName} 
                  onChange={e => setFormData({...formData, contactName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">联系电话</label>
                <input 
                  type="text" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  autoComplete="new-password" value={formData.contactPhone} 
                  onChange={e => setFormData({...formData, contactPhone: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">联系人邮箱</label>
                <input 
                  type="email" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  autoComplete="off" value={formData.contactEmail} 
                  onChange={e => setFormData({...formData, contactEmail: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">合同到期时间</label>
                <input 
                  type="date" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  value={formData.contractEndDate} 
                  onChange={e => setFormData({...formData, contractEndDate: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">总额度设置</label>
                <input 
                  type="number" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  value={formData.initialQuota} 
                  onChange={e => setFormData({...formData, initialQuota: Number(e.target.value)})}
                />
              </div>
              {editingGroup && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">状态</label>
                  <select 
                    className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2"
                    value={formData.status}
                    onChange={e => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="ACTIVE">正常</option>
                    <option value="INACTIVE">禁用</option>
                  </select>
                </div>
              )}
              
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">备注信息</label>
                <textarea 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  rows="2"
                  value={formData.remark} 
                  onChange={e => setFormData({...formData, remark: e.target.value})}
                ></textarea>
              </div>
              <div className="col-span-2 flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">取消</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">保存</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quota Modal */}
      {quotaModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800/80 backdrop-blur-xl border border-gray-200 dark:border-slate-700 p-6 rounded-2xl w-full max-w-sm shadow-2xl">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">额度管理</h2>
            <div className="flex gap-2 mb-4 bg-gray-100 p-1 rounded-lg">
              <button 
                onClick={() => setQuotaModal({...quotaModal, type: 'topup'})}
                className={`flex-1 py-1 rounded-md text-sm font-medium transition-all ${quotaModal.type === 'topup' ? 'bg-white dark:bg-slate-600 shadow' : 'text-gray-500'}`}
              >
                充值 (增加上限)
              </button>
              <button 
                onClick={() => setQuotaModal({...quotaModal, type: 'refund'})}
                className={`flex-1 py-1 rounded-md text-sm font-medium transition-all ${quotaModal.type === 'refund' ? 'bg-white dark:bg-slate-600 shadow' : 'text-gray-500'}`}
              >
                退款 (减少已用)
              </button>
            </div>
            <form onSubmit={handleQuotaSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">金额/数量</label>
                <input 
                  type="number" 
                  className="w-full border dark:border-slate-600 dark:bg-slate-700/50 dark:text-gray-200 rounded-lg p-2" 
                  value={quotaAmount} 
                  onChange={e => setQuotaAmount(Number(e.target.value))}
                  required
                  min="1"
                />
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" onClick={() => setQuotaModal({...quotaModal, open: false})} className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">取消</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">确认</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
